import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';

interface MiniBestestScoreField {
  controlName: string;
  maxScore: number;
  // One entry per scored item. Items answered per side (3, 6) or per TUG run
  // (14) list both questions and count the worse (lower) answer.
  items: string[][];
}

// Same pattern the backend uses, so the live sub scores match its score.
const MINI_BESTEST_POINTS_REGEX = /^\s*\(\s*([0-2])\s*\)/;

const MINI_BESTEST_SUB_SCORES: MiniBestestScoreField[] = [
  // Anticipatory
  {
    controlName: 'question694',
    maxScore: 6,
    items: [['question689'], ['question690'], ['question692', 'question693']],
  },
  // Reactive postural control
  {
    controlName: 'question701',
    maxScore: 6,
    items: [['question696'], ['question697'], ['question699', 'question700']],
  },
  // Sensory orientation
  {
    controlName: 'question706',
    maxScore: 6,
    items: [['question703'], ['question704'], ['question705']],
  },
  // Dynamic gait
  {
    controlName: 'question715',
    maxScore: 10,
    items: [
      ['question708'],
      ['question709'],
      ['question710'],
      ['question711'],
      ['question713', 'question714'],
    ],
  },
];

// Items 3, 6 and 14 are answered twice: per side, or per TUG run.
const MINI_BESTEST_PAIRS: string[][] = MINI_BESTEST_SUB_SCORES.flatMap(
  (subScore) => subScore.items,
).filter((item) => item.length > 1);

const MINI_BESTEST_TOTAL_CONTROL_NAME = 'question716';
const MINI_BESTEST_TOTAL_MAX_SCORE = 28;

@Component({
  selector: 'app-mini-bestest-questions',
  templateUrl: './mini-bestest-questions.component.html',
  styleUrl: './mini-bestest-questions.component.css',
  standalone: false,
})
export class MiniBestestQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private sub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateScores();
    });
    this.recalculateScores();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split('||')[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split('||')[1]?.trim() ?? '';
  }

  // The template introduces items 3, 6 and 14 with a section element holding
  // the item's title, so each pair is shown as one question. Returns the
  // pair's two questions, or [] for a real section.
  getPairQuestions(element: IQuestionnaireQuestion): IQuestionnaireQuestion[] {
    if (element.type !== 'section') {
      return [];
    }

    const next = this.elements[this.elements.indexOf(element) + 1];
    const pair = MINI_BESTEST_PAIRS.find((names) => names[0] === next?.name);

    return pair ? this.elements.filter((el) => pair.includes(el.name)) : [];
  }

  // True for a question already shown inside its item's row.
  isPairQuestion(element: IQuestionnaireQuestion): boolean {
    const pair = MINI_BESTEST_PAIRS.find((names) =>
      names.includes(element.name),
    );
    if (!pair) {
      return false;
    }

    const firstIndex = this.elements.findIndex((el) => el.name === pair[0]);
    return this.elements[firstIndex - 1]?.type === 'section';
  }

  isScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.type === 'text' && this.getMaxScore(element.name) !== null;
  }

  isTotalScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.name === MINI_BESTEST_TOTAL_CONTROL_NAME;
  }

  // The template has no maximums, so the UI supplies them.
  getMaxScore(controlName: string): number | null {
    if (controlName === MINI_BESTEST_TOTAL_CONTROL_NAME) {
      return MINI_BESTEST_TOTAL_MAX_SCORE;
    }

    return (
      MINI_BESTEST_SUB_SCORES.find(
        (subScore) => subScore.controlName === controlName,
      )?.maxScore ?? null
    );
  }

  getScoreDisplay(controlName: string): string {
    const value = this.formGroup.get(controlName)?.value;
    return value === null || value === undefined || value === ''
      ? '0'
      : String(value);
  }

  private recalculateScores(): void {
    const subScores = MINI_BESTEST_SUB_SCORES.map((subScore) => {
      const score = this.calculateSubScore(subScore);
      this.setLockedScore(subScore.controlName, score);
      return score;
    });

    // Live total of the sections answered so far.
    const answeredSubScores = subScores.filter(
      (score): score is number => score !== null,
    );
    const total = answeredSubScores.length
      ? answeredSubScores.reduce((sum, score) => sum + score, 0)
      : null;
    this.setLockedScore(MINI_BESTEST_TOTAL_CONTROL_NAME, total);

    this.cdr.detectChanges();
  }

  // Live sub score: sums the items answered so far, or null while none is,
  // so an untouched section stays empty and is not saved.
  private calculateSubScore(subScore: MiniBestestScoreField): number | null {
    let total: number | null = null;

    for (const item of subScore.items) {
      const points = item
        .map((controlName) => this.getSelectedPoints(controlName))
        .filter((point): point is number => point !== null);

      if (!points.length) {
        continue;
      }

      // A pair counts its worse answer so far.
      total = (total ?? 0) + Math.min(...points);
    }

    return total;
  }

  // Points come from the selected choice text, never from its position.
  private getSelectedPoints(controlName: string): number | null {
    const selectedValue = this.formGroup.get(controlName)?.value;
    if (
      selectedValue === null ||
      selectedValue === undefined ||
      selectedValue === ''
    ) {
      return null;
    }

    const choice = (
      this.elements?.find((el) => el.name === controlName)?.choices ?? []
    ).find((c: IQuestionnaireChoice) => c.value === selectedValue);
    const match = (choice?.text ?? '').match(MINI_BESTEST_POINTS_REGEX);

    return match ? Number(match[1]) : null;
  }

  private setLockedScore(controlName: string, score: number | null): void {
    const control = this.formGroup.get(controlName);
    if (!control) return;

    control.setValue(score === null ? '' : String(score), {
      emitEvent: false,
    });
    control.disable({ emitEvent: false });
  }
}
